import React, { useState, useRef, ChangeEvent, DragEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as pdfjsLib from 'pdfjs-dist';
import './MediaUpload.css';

// Configure PDF.js worker - use local worker file from public directory
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface MediaUploadProps {
  botId: string;
  onMediaUrlsChange: (media: {
    original: string;
    preview: string;
    fileName: string;
    fileSize: number;
    pageCount?: number;
    type: 'image' | 'pdf';
  } | null) => void;
  disabled?: boolean;
  resetTrigger?: number;
}

interface UploadProgress {
  original: number;
  preview: number;
}

type MediaType = 'image' | 'pdf' | 'unsupported';

/**
 * Detect file type based on MIME type
 */
function detectFileType(file: File): MediaType {
  if (file.type === 'application/pdf') return 'pdf';
  if (['image/jpeg', 'image/png'].includes(file.type)) return 'image';
  return 'unsupported';
}

/**
 * Resize image to have maximum width of 1024px while maintaining aspect ratio
 * Returns a File object with resized image
 */
async function resizeToMaxWidth(file: File, maxWidth: number = 1024): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = async () => {
      let width = img.width;
      let height = img.height;

      // Only resize if width exceeds maxWidth
      if (width > maxWidth) {
        const aspectRatio = height / width;
        width = maxWidth;
        height = Math.round(maxWidth * aspectRatio);
        console.log(`Resizing image from ${img.width}x${img.height} to ${width}x${height}`);
      } else {
        console.log(`Image width ${width}px is within limit, no resizing needed`);
      }

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create resized image blob'));
            return;
          }

          const resizedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now()
          });

          console.log(`Resized image size: ${(resizedFile.size / 1024 / 1024).toFixed(2)}MB`);
          resolve(resizedFile);
        },
        file.type,
        0.95
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate thumbnail from image file or PDF first page
 * - Enforces max width of 1024px (LINE API requirement)
 * - Resizes to fit within 1MB while maintaining aspect ratio
 */
async function generateThumbnail(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = async () => {
      // STEP 1: Enforce max width of 1024px
      let width = img.width;
      let height = img.height;

      if (width > 1024) {
        const aspectRatio = height / width;
        width = 1024;
        height = Math.round(1024 * aspectRatio);
        console.log(`Preview: Resizing from ${img.width}x${img.height} to ${width}x${height}`);
      }

      // STEP 2: Enforce max height of 1024px
      if (height > 1024) {
        const aspectRatio = width / height;
        height = 1024;
        width = Math.round(1024 * aspectRatio);
        console.log(`Preview: Further resizing to ${width}x${height} to fit 1024x1024`);
      }

      // STEP 3: Compress to get under 1MB
      let quality = 0.85;
      const MAX_PREVIEW_SIZE = 1048576; // 1MB

      const generateBlob = async (): Promise<Blob | null> => {
        canvas.width = width;
        canvas.height = height;
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        return new Promise((res) => {
          canvas.toBlob((blob) => res(blob), file.type, quality);
        });
      };

      let blob = await generateBlob();
      let iterations = 0;
      const maxIterations = 20;

      while (blob && blob.size > MAX_PREVIEW_SIZE && iterations < maxIterations) {
        if (quality > 0.5) {
          quality -= 0.05;
        } else {
          width = Math.floor(width * 0.9);
          height = Math.floor(height * 0.9);
          if (width < 100 || height < 100) break;
        }
        blob = await generateBlob();
        iterations++;
      }

      if (!blob) {
        reject(new Error('Failed to generate thumbnail'));
        return;
      }

      if (blob.size > MAX_PREVIEW_SIZE) {
        reject(new Error('Could not compress thumbnail below 1MB'));
        return;
      }

      console.log(`Preview final size: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);

      const thumbnailFile = new File([blob], `preview-${file.name}`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      resolve(thumbnailFile);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Generate preview image from PDF first page
 */
async function generatePdfPreview(file: File): Promise<File> {
  try {
    console.log('Loading PDF for preview generation...');

    // Load PDF
    const loadingTask = pdfjsLib.getDocument(URL.createObjectURL(file));
    const pdf = await loadingTask.promise;

    console.log(`PDF loaded: ${pdf.numPages} pages`);

    // Get first page
    const page = await pdf.getPage(1);

    // Render to canvas at 2x scale for quality
    const viewport = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Could not get canvas context');
    }

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    console.log(`Rendering PDF page at ${viewport.width}x${viewport.height}`);

    await page.render({
      canvasContext: context,
      viewport: viewport,
      intent: 'display'
    } as any).promise;

    console.log('PDF page rendered to canvas');

    // Convert to JPEG blob
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to create blob from canvas'));
        },
        'image/jpeg',
        0.85
      );
    });

    const previewFile = new File([blob], 'preview.jpg', { type: 'image/jpeg' });
    console.log(`PDF preview generated: ${(previewFile.size / 1024 / 1024).toFixed(2)}MB`);

    // If preview is > 1MB, compress it using existing thumbnail logic
    if (previewFile.size > 1024 * 1024) {
      console.log('Preview > 1MB, compressing...');
      return await generateThumbnail(previewFile);
    }

    return previewFile;
  } catch (error) {
    console.error('PDF preview generation failed:', error);
    throw new Error(`Failed to generate PDF preview: ${(error as Error).message}`);
  }
}

export const MediaUpload: React.FC<MediaUploadProps> = ({
  botId,
  onMediaUrlsChange,
  disabled = false,
  resetTrigger = 0
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mediaType, setMediaType] = useState<MediaType | null>(null);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState<number | null>(null);
  const [uploadedUrls, setUploadedUrls] = useState<{ original: string; preview: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({ original: 0, preview: 0 });
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  /**
   * Reset component when resetTrigger changes
   */
  useEffect(() => {
    if (resetTrigger > 0) {
      setSelectedFile(null);
      setMediaType(null);
      setPreviewFile(null);
      setPreviewUrl(null);
      setPdfPageCount(null);
      setUploadedUrls(null);
      setError(null);
      setUploadProgress({ original: 0, preview: 0 });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [resetTrigger]);

  /**
   * Validate file based on type
   */
  const validateFile = (file: File, type: MediaType): string | null => {
    if (type === 'unsupported') {
      return t('mediaUpload.invalidFileType');
    }

    if (type === 'image') {
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        return t('mediaUpload.invalidImageType');
      }
      if (file.size > 10 * 1024 * 1024) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        return t('mediaUpload.imageTooLarge', { size: sizeMB });
      }
    }

    if (type === 'pdf') {
      if (file.type !== 'application/pdf') {
        return t('mediaUpload.invalidPdfType');
      }
      if (file.size > 50 * 1024 * 1024) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(2);
        return t('mediaUpload.pdfTooLarge', { size: sizeMB });
      }
    }

    return null;
  };

  /**
   * Upload media to S3 using presigned URLs
   */
  const uploadMedia = async (original: File, preview: File, type: MediaType, pageCount?: number) => {
    try {
      const { adminApiService } = await import('../../services/adminApiService');

      // Choose API method based on type
      const response = type === 'pdf'
        ? await adminApiService.generateFileUploadUrl(
            botId,
            original.name,
            original.size,
            preview.size,
            original.type
          )
        : await adminApiService.generateImageUploadUrl(
            botId,
            original.name,
            original.size,
            preview.size,
            original.type
          );

      // Normalize response (different field names for images vs PDFs)
      const normalizedResponse = type === 'pdf'
        ? {
            originalUploadUrl: (response as any).fileUploadUrl,
            previewUploadUrl: (response as any).previewUploadUrl,
            originalUrl: (response as any).fileUrl,
            previewUrl: (response as any).previewUrl
          }
        : {
            originalUploadUrl: (response as any).originalUploadUrl,
            previewUploadUrl: (response as any).previewUploadUrl,
            originalUrl: (response as any).originalImageUrl,
            previewUrl: (response as any).previewImageUrl
          };

      // Upload both in parallel
      await Promise.all([
        adminApiService.uploadImageToS3(
          normalizedResponse.originalUploadUrl,
          original,
          (progress) => setUploadProgress(prev => ({ ...prev, original: progress }))
        ),
        adminApiService.uploadImageToS3(
          normalizedResponse.previewUploadUrl,
          preview,
          (progress) => setUploadProgress(prev => ({ ...prev, preview: progress }))
        )
      ]);

      // Success - notify parent with normalized URLs
      setUploadedUrls({
        original: normalizedResponse.originalUrl,
        preview: normalizedResponse.previewUrl
      });

      onMediaUrlsChange({
        original: normalizedResponse.originalUrl,
        preview: normalizedResponse.previewUrl,
        fileName: original.name,
        fileSize: original.size,
        pageCount,
        type: type as 'image' | 'pdf'
      });

      setError(null);
    } catch (err) {
      const errorMessage = (err as Error).message || t('mediaUpload.uploadFailed');
      setError(errorMessage);
      setUploadedUrls(null);
      onMediaUrlsChange(null);
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (file: File) => {
    const type = detectFileType(file);

    const validationError = validateFile(file, type);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
    setMediaType(type);
    setError(null);
    setUploadProgress({ original: 0, preview: 0 });

    // Page count is also sent with the push so the LINE message can say 全Nページ.
    // Kept in a local (not just state) because uploadMedia below runs in this
    // closure and would read the pre-update state value.
    let pageCount: number | undefined;

    // Generate preview for UI
    if (type === 'image') {
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
    } else if (type === 'pdf') {
      // Get PDF page count
      try {
        const pdf = await pdfjsLib.getDocument(URL.createObjectURL(file)).promise;
        pageCount = pdf.numPages;
        setPdfPageCount(pdf.numPages);
      } catch (err) {
        console.error('Failed to get PDF info:', err);
      }
    }

    // Process and upload
    try {
      setUploading(true);

      let processedOriginal: File;
      let preview: File;

      if (type === 'image') {
        // Resize original image to max 1024px width
        processedOriginal = await resizeToMaxWidth(file, 1024);
        // Generate thumbnail from the resized original
        preview = await generateThumbnail(processedOriginal);
        setPreviewFile(preview);
      } else if (type === 'pdf') {
        // PDF stays as-is
        processedOriginal = file;
        // Generate preview from first page
        preview = await generatePdfPreview(file);
        setPreviewFile(preview);

        // Set preview URL for display
        const previewReader = new FileReader();
        previewReader.onload = (e) => setPreviewUrl(e.target?.result as string);
        previewReader.readAsDataURL(preview);
      } else {
        throw new Error('Unsupported file type');
      }

      // Start upload
      await uploadMedia(processedOriginal, preview, type, pageCount);
    } catch (err) {
      const errorMessage = (err as Error).message || t('mediaUpload.processingFailed');
      setError(errorMessage);
      setUploading(false);
    }
  };

  /**
   * Handle file input change
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle drag and drop
   */
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  /**
   * Handle remove media
   */
  const handleRemove = () => {
    setSelectedFile(null);
    setMediaType(null);
    setPreviewFile(null);
    setPreviewUrl(null);
    setPdfPageCount(null);
    setUploadedUrls(null);
    setError(null);
    setUploadProgress({ original: 0, preview: 0 });
    onMediaUrlsChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle click on dropzone
   */
  const handleDropzoneClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  // Get appropriate label based on what's uploaded
  const getLabel = () => {
    if (mediaType === 'pdf') {
      return t('mediaUpload.pdfUpload');
    }
    return t('mediaUpload.imageUpload');
  };

  const getDescription = () => {
    if (mediaType === 'pdf') {
      return t('mediaUpload.pdfUploadDescription');
    }
    return t('mediaUpload.imageUploadDescription');
  };

  return (
    <div className="media-upload-container">
      <label className="media-upload-label">
        {selectedFile ? getLabel() : t('mediaUpload.mediaUpload')}
      </label>
      <p className="media-upload-description">
        {selectedFile ? getDescription() : t('mediaUpload.mediaUploadDescription')}
      </p>

      {!previewUrl && (
        <div
          className={`media-upload-dropzone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleDropzoneClick}
        >
          <svg className="media-upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <p>{t('mediaUpload.dragDropMedia')}</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            onChange={handleInputChange}
            disabled={disabled}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {previewUrl && (
        <div className="media-upload-preview-container">
          <div className="media-upload-preview">
            <img src={previewUrl} alt="Preview" className="media-upload-preview-img" />
            {!uploading && uploadedUrls && (
              <button
                type="button"
                onClick={handleRemove}
                className="media-upload-remove-btn"
                disabled={disabled}
              >
                ✕
              </button>
            )}
          </div>

          {/* File info for PDFs */}
          {mediaType === 'pdf' && selectedFile && (
            <div className="media-upload-file-info">
              <p className="media-upload-file-name">
                📄 {selectedFile.name}
              </p>
              <p className="media-upload-file-meta">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                {pdfPageCount && ` • ${t('mediaUpload.pdfPages', { count: pdfPageCount })}`}
              </p>
            </div>
          )}

          {uploading && (
            <div className="media-upload-progress-wrapper">
              <div className="media-upload-progress-item">
                <label className="media-upload-progress-label">
                  {mediaType === 'pdf'
                    ? t('mediaUpload.uploadingFile', { progress: uploadProgress.original })
                    : t('mediaUpload.uploadingOriginal', { progress: uploadProgress.original })
                  }
                </label>
                <div className="media-upload-progress-bar">
                  <div
                    className="media-upload-progress-fill"
                    style={{ width: `${uploadProgress.original}%` }}
                  />
                </div>
              </div>
              <div className="media-upload-progress-item">
                <label className="media-upload-progress-label">
                  {t('mediaUpload.uploadingPreview', { progress: uploadProgress.preview })}
                </label>
                <div className="media-upload-progress-bar">
                  <div
                    className="media-upload-progress-fill"
                    style={{ width: `${uploadProgress.preview}%` }}
                  />
                </div>
              </div>
            </div>
          )}

          {uploadedUrls && !uploading && (
            <p className="media-upload-success">
              {mediaType === 'pdf' ? t('mediaUpload.pdfUploaded') : t('mediaUpload.imageUploaded')}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="media-upload-error">
          {error}
        </div>
      )}
    </div>
  );
};
